import {
    AccountType,
    IAssetAllocationTreeNode,
    IBenchmarkReturn, IFeesTreeNode,
    IPerformanceTreeNode,
    IPlanTreeNode,
    IPlanTreeTargetAllocation, IPortfolioPerformance, ITreeNodeBase,
} from "../../model/Portfolio.model";
import {compareDates, formatMonthYearDate, getInviewDateString, parseDate} from "../../utils/dateUtil";
import {roundNumber} from "../../utils/numberUtil";
import {ISelectValue} from "../common/Select.component";
import {useTheme} from "@table-library/react-table-library/theme";
import {getTheme} from "@table-library/react-table-library/baseline";
import {AonColors} from "../../../../css/Colors";
import {LINE_GAP_MULTIPLIER} from "./TargetAllocationChart";
import {
    IAssetAllocation,
    IAssetAllocationComparison,
    IBuildingBlock,
    IBuildingBlockComparison, ILiquidityProfile,
    ILiquidityProfileComparison,
    IModelPortfolioComparison,
    IPortfolio, IPortfolioFeesTreeNode, PARIS_PORTFOLIO_PLAN_TYPE
} from "../../api/PortfolioApi";
import {hasConsultantLevelAccess, isClientUser} from "../../utils/sessionUtil";

export enum DonutChartColors {
    Steelblue = "#0084BB",
    Purple = "#6E027F",
    Cadetblue = "#29B0C3",
    Midnightblue = "#101E7F",
    Mediumvioletred = "#A70070",
    Mediumturquoise = "#73E2D8",
    Gold = "#FFC300",
    Darkseagreen = "#97ab72",
    Darkslateblue = "#581845",
    Lightseagreen = "#58ada6",
    Darkslategray = "#193f63",
    Chocolate = "#d47502"
}


export enum PerformanceType {
    RELATIVE,
    ABSOLUTE,
}
export enum RelativeReturnClassName {
    GREEN = "green",
    YELLOW = "yellow",
    RED = "red",
}

export enum EllipsePercentilePosition {
    MISSING_EFFECTIVE_NET_EXPENSE_RATIO = 0,
    BELOW_25TH_PERCENTILE = 38,
    AT_25TH_PERCENTILE = 88,
    BETWEEN_25TH_AND_50TH_PERCENTILE = 138,
    AT_50TH_PERCENTILE = 188,
    BETWEEN_50TH_AND_75TH_PERCENTILE = 238,
    AT_75TH_PERCENTILE = 288,
    ABOVE_75TH_PERCENTILE = 338,
}

export interface IBarChartData {
    name: string;
    absolutePerformanceValue: number | string;
    benchmarkPerformanceValue: number | string;
}

export const DC_PLAN_TYPES = ["Corporate DC", "Public DC", "Taft-Hartley DC", "Exec NQDC",
    "401(a) Governmental", "403(a)", "403(b)", "457"];

export const UNCATEGORIZED = "Uncategorized";
export const UNKNOWN = "Unknown";
export const LIQUIDITY_PROFILE_LIQUID = "Liquid";
export const LIQUIDITY_PROFILE_QUASI_LIQUID = "Quasi-Liquid";
export const LIQUIDITY_PROFILE_ILLIQUID = "Illiquid";
type PerformancePeriodKey = "quarter" | "ytd" | "year" | "threeYears" | "fiveYears" | "tenYears";

export const getTotalNode = (flatNodes: ITreeNodeBase[], date: string | undefined = undefined): ITreeNodeBase | undefined => {
    const nodes = date ? flatNodes.filter(node => node.date === date) : flatNodes;
    return nodes.find(node => node.parentId === null);
};

export const buildAssetAllocationTreeByDate = (datedFlatNodes: IPlanTreeNode[], date: string): IAssetAllocationTreeNode[] => {
    const flatNodes = datedFlatNodes.filter(node => node.date === date);
    if (flatNodes.length === 0) {
        return [];
    }
    const totalPlanNode = getTotalNode(flatNodes);
    return totalPlanNode ? getAssetAllocationChildNodes(flatNodes, totalPlanNode.id) : [];
};

export const buildPerformanceTreeByDate = (datedFlatNodes: IPlanTreeNode[], date: string, datedBenchmarkReturns: IBenchmarkReturn[] = []): IPerformanceTreeNode[] => {
    const flatNodes = datedFlatNodes.filter(node => node.date === date);

    if (flatNodes.length === 0) {
        return [];
    }
    const benchmarks = datedBenchmarkReturns.filter(benchmark => benchmark.date === date);
    const totalPlanNode = getTotalNode(flatNodes);
    return totalPlanNode ? getPerformanceChildNodes(flatNodes, totalPlanNode.id, benchmarks) : [];
};

export const buildFeeTree = (flatNodes: IPortfolioFeesTreeNode[]): IFeesTreeNode[] => {
    const totalFeeNode = getTotalNode(flatNodes);
    return totalFeeNode ? getFeeChildNodes(flatNodes, totalFeeNode.id) : [];
};

const getBenchmarkNode = (parentNode: IPlanTreeNode, benchmarkReturns: IBenchmarkReturn[])
    : IPerformanceTreeNode[] | null => {

    const benchmarks = benchmarkReturns.filter(it => it.benchmarkId === parentNode.benchmarkId);
    if (benchmarks.length === 0) {
        return null;
    }


    return benchmarks
        .map(benchmark => ({
            id: parentNode.id + 100*benchmark.benchmarkId,
            name: benchmark.name,
            sortOrder: 1,
            accountType: AccountType.BENCHMARK,
            date: benchmark.date,
            nodes: null,
            relativePerformance: benchmark.performance,
            absolutePerformance: benchmark.performance
        }));
};

const getPerformanceChildNodes = (flatNodes: IPlanTreeNode[], parentId: number, benchmarkReturns: IBenchmarkReturn[])
    : IPerformanceTreeNode[] => {
    return flatNodes
        .filter(it => it.parentId === parentId)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(node => ({
            id: node.id,
            name: node.name,
            sortOrder: node.sortOrder,
            accountType: node.accountType,
            date: node.date,
            backstopId: node.backstopId,
            nodes: node.accountType === AccountType.ATOMIC
                ? benchmarkReturns.length > 0 ? getBenchmarkNode(node, benchmarkReturns) : null
                : getPerformanceChildNodes(flatNodes, node.id, benchmarkReturns),
            relativePerformance: node.relativePerformance,
            absolutePerformance: node.absolutePerformance,
            managementType: node.managementType,
        }));
};

const getFeeChildNodes = (flatNodes: IPortfolioFeesTreeNode[], parentId: number): IFeesTreeNode[] => {
    return flatNodes
        .filter(it => it.parentId === parentId)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(node => ({
            id: node.id,
            name: node.name,
            sortOrder: node.sortOrder,
            accountType: node.accountType,
            date: node.date,
            backstopId: node.backstopId,
            peerUniverseName: node.peerUniverseName,
            netExpenseRatio: node.netExpenseRatio,
            revenueShare: node.revenueShare,
            effectiveNetExpenseRatio: node.effectiveNetExpenseRatio,
            peerUniverseMedian: node.peerUniverseMedian,
            percentile25: node.percentile25,
            percentile50: node.percentile50,
            percentile75: node.percentile75,
            population: node.population,
            nodes: node.accountType === AccountType.ATOMIC ? null : getFeeChildNodes(flatNodes, node.id),
        } as IFeesTreeNode));
};

const getAssetAllocationChildNodes = (flatNodes: IPlanTreeNode[], parentId: number): IAssetAllocationTreeNode[] => {
    return flatNodes
        .filter(it => it.parentId === parentId)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(node => ({
            id: node.id,
            name: node.name,
            sortOrder: node.sortOrder,
            accountType: node.accountType,
            marketValue: node.marketValue,
            marketValuePctOfTotal: node.marketValuePctOfTotal,
            marketValuePctOfParent: node.marketValuePctOfParent,
            backstopId: node.backstopId,
            nodes: node.accountType === AccountType.ATOMIC ? null : getAssetAllocationChildNodes(flatNodes, node.id),
            rating: node.rating ? node.rating : "",
            priorMarketValuePctOfTotal: node.priorMarketValuePctOfTotal,
            date: node.date,
        }));
};

export const calculateColorAndOpacity = (index: number) => {
    const colors = [
        DonutChartColors.Steelblue,
        DonutChartColors.Purple,
        DonutChartColors.Cadetblue,
        DonutChartColors.Midnightblue,
        DonutChartColors.Mediumvioletred,
        DonutChartColors.Mediumturquoise,
        DonutChartColors.Gold,
        DonutChartColors.Darkseagreen,
        DonutChartColors.Darkslateblue,
        DonutChartColors.Lightseagreen,
        DonutChartColors.Darkslategray,
        DonutChartColors.Chocolate,
    ];

    const rounds = Math.trunc(index / colors.length);
    const calculatedIndex = index % colors.length;

    return {fill: colors[calculatedIndex], opacity: 1 / Math.pow(2, rounds)};
};

export const getPtcOfTotalPriorPeriod = (node: IPlanTreeNode, flatNodes: IPlanTreeNode[])
    : number | undefined => {
    const nodeDate = parseDate(node.date.toString());
    const priorNodes = flatNodes
        .filter(flatNode => flatNode.id === node.id
            && compareDates(parseDate(flatNode.date.toString()), nodeDate) > 0)
        .sort((a, b) => compareDates(parseDate(a.date.toString()), parseDate(b.date.toString())));
    if (priorNodes.length > 0) {
        return roundNumber(node.marketValuePctOfTotal) - roundNumber(priorNodes[0].marketValuePctOfTotal);
    }
    return undefined;
};

export const getDateOptions = (nodes: IPlanTreeNode[]): ISelectValue[] => {
    const dates = Array.from(new Set(nodes
        .map((node) => node.date)))
        .sort((a, b) => compareDates(parseDate(a.toString()), parseDate(b.toString())));

    return dates.map((date) => (
        {name: formatMonthYearDate(date), id: date})
    );
};

export const PLAN_TREE_THEME = (templateColumns: string) => {
    return useTheme([
        getTheme(),
        {
            Table: `
            --data-table-library_grid-template-columns:  ${templateColumns};
            border: 1px solid ${AonColors.AonGray05};
            max-height: 662px;
          `,
            HeaderRow: `
            height: 100px;
          `,
            Row: `
             border-top: 1px solid ${AonColors.AonGray05};
             border-bottom: 1px solid ${AonColors.AonGray05};
             font-face: Helvetica Now Text;
             font-weight: 400;
             font-size: 14px;
             line-height: 18px;
             bottom-margin: 12px;
             color: ${AonColors.AonNavy};             
            `,
            HeaderCell: `
            font-face: Helvetica Now Text;
            font-weight: 700;
            font-size: 14px;
            line-height: 18px;
            bottom-margin: 12px;
            color: ${AonColors.AonNavy};
            border-bottom: 1px solid ${AonColors.AonGray05};
            border-right: 1px solid ${AonColors.AonGray05};
            cursor: default;
            align-items: flex-end;
            padding: 12px;
            &:nth-last-of-type(1) {
                border-right: none;
            }
            &:nth-of-type(4) {
                div {
                    overflow: visible;
                    white-space: normal;
                    text-overflow: clip;
                    word-wrap: break-word;
                }
            }
            `,
            BaseCell: `
            height: 60px;
            text-align: right;
            &:nth-of-type(1) {
                text-align: left;
                div {
                    white-space: normal;
                }
            }
            &:nth-of-type(2) {
                text-align: right;
            }
          `,
            Cell: `
            cursor: default;
            &:a {
               cursor: pointer;
            }
            `,
        },
    ]);
};

export const getClassNameForManagementType
    = (performanceType: PerformanceType, valueToDisplay: string, managementType: string | undefined) => {
    if(performanceType === PerformanceType.RELATIVE) {
        const relativeReturn = parseFloat(valueToDisplay);
        if(managementType && managementType.toLowerCase() === "passive") {
            if(relativeReturn > -0.5) return RelativeReturnClassName.GREEN;
            else if(relativeReturn <= -0.5 && relativeReturn > -1) return RelativeReturnClassName.YELLOW;
            else if(relativeReturn <= -1) return RelativeReturnClassName.RED;
        } else {
            if(relativeReturn >= 0) return RelativeReturnClassName.GREEN;
            else if(relativeReturn < 0 && relativeReturn > -1) return RelativeReturnClassName.YELLOW;
            else if(relativeReturn <= -1) return RelativeReturnClassName.RED;
        }
    }
    return "";
};

export const getTargetAllocationsByPeriod =
    ( selectedDate: ISelectValue | undefined, targetAllocations: IPlanTreeTargetAllocation[])
    : IPlanTreeTargetAllocation[] => {
    const selectedDateFormated = selectedDate ? getInviewDateString(selectedDate.id as string) : "";
    return targetAllocations.filter(node => node.asOfDate === selectedDateFormated)
        .map(node => { return {...node, marketValuePctOfTotal: roundNumber(node.marketValuePctOfTotal, 2)};});
};

export const breakString = (text: string, maxLength: number): string[] => {
    if (text.length <= maxLength) return [text];

    const lines: string[] = [];
    let currentLine = "";

    text.split(" ").forEach((word) => {
        if ((currentLine + word).length > maxLength) {
            lines.push(currentLine.trim());
            currentLine = word + " ";
        } else {
            currentLine += word + " ";
        }
    });

    if (currentLine.trim()) lines.push(currentLine.trim());

    return lines;
};

export const getMaxLabelWidth = (targetAllocations: IPlanTreeTargetAllocation[], maxNumberOfCharsPerLine: number): number => {
    const ICON_WIDTH = 20;
    const GAP_ICON_LABEL = 32;
    const CAPITAL_LETTER_MULTIPLIER = 10;
    const SMALL_LETTER_MULTIPLIER = 7;
    const accountNames = targetAllocations.map(it => it.accountName);
    const maxLabelPerLine = accountNames.reduce((maxLabel, name) => {
      const maxLabelPerLineOfAccountName = breakString(name, maxNumberOfCharsPerLine)
          .reduce((maxLabelOfAccountName, line) => {
            return line.length > maxLabelOfAccountName.length ? line : maxLabelOfAccountName;
          }, "");

      return maxLabel.length > maxLabelPerLineOfAccountName.length ? maxLabel : maxLabelPerLineOfAccountName;
    }, "");

    const capitalCount = Array.from(maxLabelPerLine).filter(char => char >= 'A' && char <= 'Z').length;
    return capitalCount * CAPITAL_LETTER_MULTIPLIER + (maxLabelPerLine.length - capitalCount) * SMALL_LETTER_MULTIPLIER
        + ICON_WIDTH + GAP_ICON_LABEL;
};

export const getChartHeight = (targetAllocations: IPlanTreeTargetAllocation[], maxNumberOfCharsPerLine: number): number => {
        const FONT_SIZE = 16;
        const LEGEND_HEIGHT = 100;
        const NUMBER_OF_BARS_PER_LABEL = 2;
        const BAR_HEIGHT = 30;
        const maxNumberOfChars = Math.max(...targetAllocations.map(it => it.accountName.length));
        const maxNumberOfLines = Math.ceil(maxNumberOfChars / maxNumberOfCharsPerLine);
        const maxLabelHeight = maxNumberOfLines * LINE_GAP_MULTIPLIER * FONT_SIZE * targetAllocations.length;
        const totalBarHeight = targetAllocations.length * NUMBER_OF_BARS_PER_LABEL * BAR_HEIGHT * LINE_GAP_MULTIPLIER;
        return Math.max(totalBarHeight, maxLabelHeight) + LEGEND_HEIGHT;
};

const getPlanAssetAllocationComparisonFromAssetAllocation
    = (planAssetAllocation: IAssetAllocation): IAssetAllocationComparison => ({
    assetClassId: planAssetAllocation.assetClassId,
    assetClass: planAssetAllocation.assetClass,
    marketValuePctOfTotal: planAssetAllocation.marketValuePctOfTotal,
    modelMarketValuePctOfTotal: null,
});

const getPlanBuildingBlockComparisonFromBuildingBlock
    = (planBuildingBlock: IBuildingBlock): IBuildingBlockComparison => ({
    name: planBuildingBlock.name,
    assetAllocations: planBuildingBlock.assetAllocations.map(getPlanAssetAllocationComparisonFromAssetAllocation),
    totalPercentage: planBuildingBlock.totalPercentage,
    modelTotalPercentage: null,
});

const getPlanLiquidityProfileComparisonFromLiquidityProfile
    = (planLiquidityProfile: ILiquidityProfile): ILiquidityProfileComparison => ({
    name: planLiquidityProfile.name,
    totalPercentage: planLiquidityProfile.totalPercentage,
    modelTotalPercentage: null,
    buildingBlocks: planLiquidityProfile.buildingBlocks.map(getPlanBuildingBlockComparisonFromBuildingBlock),
});

const getModelPortfolioComparisonFromModelPortfolio = (modelPortfolio: IPortfolio): IModelPortfolioComparison => ({
    id: modelPortfolio.id,
    name: modelPortfolio.name,
    liquidityProfiles: modelPortfolio.liquidityProfiles.map(modelLiquidityProfile => ({
        name: modelLiquidityProfile.name,
        totalPercentage: null,
        modelTotalPercentage: modelLiquidityProfile.totalPercentage,
        buildingBlocks: modelLiquidityProfile.buildingBlocks.map(modelBuildingBlock => ({
            name: modelBuildingBlock.name,
            totalPercentage: null,
            modelTotalPercentage: modelBuildingBlock.totalPercentage,
            assetAllocations: modelBuildingBlock.assetAllocations.map(modelAssetAllocation => ({
                assetClassId: modelAssetAllocation.assetClassId,
                assetClass: modelAssetAllocation.assetClass,
                marketValuePctOfTotal: null,
                modelMarketValuePctOfTotal: modelAssetAllocation.marketValuePctOfTotal,
            }))
        }))
    }))
});

export const getPortfolioComparison = (planPortfolio: IPortfolio, modelPortfolio: IPortfolio): IModelPortfolioComparison => {
    const modelPortfolioComparison = getModelPortfolioComparisonFromModelPortfolio(modelPortfolio);

    planPortfolio.liquidityProfiles.forEach(planLiquidityProfile => {
        const modelLiquidityProfileComparison = modelPortfolioComparison.liquidityProfiles
            .find(lp => lp.name === planLiquidityProfile.name);

        if (!modelLiquidityProfileComparison) {
            modelPortfolioComparison.liquidityProfiles
                .push(getPlanLiquidityProfileComparisonFromLiquidityProfile(planLiquidityProfile));
            return;
        }

        modelLiquidityProfileComparison.totalPercentage = planLiquidityProfile.totalPercentage;

        planLiquidityProfile.buildingBlocks.forEach(planBuildingBlock => {
            const modelBuildingBlockComparison = modelLiquidityProfileComparison.buildingBlocks
                .find(bb => bb.name === planBuildingBlock.name);

            if (!modelBuildingBlockComparison) {
                modelLiquidityProfileComparison.buildingBlocks
                    .push(getPlanBuildingBlockComparisonFromBuildingBlock(planBuildingBlock));
                return;
            }

            modelBuildingBlockComparison.totalPercentage = planBuildingBlock.totalPercentage;

            planBuildingBlock.assetAllocations.forEach(planAssetAllocation => {
                const modelAssetAllocationComparison = modelBuildingBlockComparison.assetAllocations
                    .find(aa => aa.assetClassId === planAssetAllocation.assetClassId);

                if (!modelAssetAllocationComparison) {
                    modelBuildingBlockComparison.assetAllocations
                        .push(getPlanAssetAllocationComparisonFromAssetAllocation(planAssetAllocation));
                    return;
                }

                modelAssetAllocationComparison.marketValuePctOfTotal = planAssetAllocation.marketValuePctOfTotal;
            });
        });
    });

    const liquidityProfileOrder = [
        LIQUIDITY_PROFILE_LIQUID,
        LIQUIDITY_PROFILE_QUASI_LIQUID,
        LIQUIDITY_PROFILE_ILLIQUID
    ];
    modelPortfolioComparison.liquidityProfiles.sort((a, b) => {
        const indexA = liquidityProfileOrder.indexOf(a.name);
        const indexB = liquidityProfileOrder.indexOf(b.name);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return 0;
    });

    modelPortfolioComparison.liquidityProfiles.forEach(liquidityProfile => {
        liquidityProfile.buildingBlocks.sort((a, b) => a.name.localeCompare(b.name));
        liquidityProfile.buildingBlocks.forEach(buildingBlock => {
            buildingBlock.assetAllocations.sort((a, b) => a.assetClass.localeCompare(b.assetClass));
        });
    });

    return modelPortfolioComparison;
};

export const hasAccessToModelPortfolioPage = (planType: string, clientModelPortfolioAccess: boolean): boolean => {
    return planHasAccessToModelPortfolioPage(planType)
        ? hasConsultantLevelAccess() || (isClientUser() && clientModelPortfolioAccess)
        : false;
};

export const planHasAccessToModelPortfolioPage = (planType: string): boolean => {
    const allowedPlanTypes = Object.values(PARIS_PORTFOLIO_PLAN_TYPE) as string[];
    return allowedPlanTypes.includes(planType);
};

export const getEllipsePosition = (node: IPortfolioFeesTreeNode): number => {
    const { effectiveNetExpenseRatio: enr, percentile25, percentile50, percentile75 } = node;

    if(!enr) return EllipsePercentilePosition.MISSING_EFFECTIVE_NET_EXPENSE_RATIO;

    if (!percentile25 || !percentile75) {
        if (enr! < percentile50!) return EllipsePercentilePosition.AT_25TH_PERCENTILE;
        if (enr! > percentile50!) return EllipsePercentilePosition.AT_75TH_PERCENTILE;
        return EllipsePercentilePosition.AT_50TH_PERCENTILE;
    }

    const thresholds = [
        { check: enr! < percentile25, position: EllipsePercentilePosition.BELOW_25TH_PERCENTILE },
        { check: enr! === percentile25!, position: EllipsePercentilePosition.AT_25TH_PERCENTILE },
        { check: enr! > percentile25! && enr! < percentile50!,
            position: EllipsePercentilePosition.BETWEEN_25TH_AND_50TH_PERCENTILE },
        { check: enr! === percentile50!, position: EllipsePercentilePosition.AT_50TH_PERCENTILE },
        { check: enr! > percentile50! && enr! < percentile75!,
            position: EllipsePercentilePosition.BETWEEN_50TH_AND_75TH_PERCENTILE },
        { check: enr! === percentile75!, position: EllipsePercentilePosition.AT_75TH_PERCENTILE },
        { check: enr! > percentile75!, position: EllipsePercentilePosition.ABOVE_75TH_PERCENTILE },
    ];

    const found = thresholds.find(t => t.check)!;
    return found.position;
};

export const getDatedBenchmarkReturn =
    (date: string, benchmarkId: number, benchmarkReturns: IBenchmarkReturn[]): IBenchmarkReturn | undefined => {
        return benchmarkReturns.find(b => b.benchmarkId === benchmarkId && b.date === date);
    };

const hasValidPerformanceData =
    (totalPlanAbsolutePerformance: IPortfolioPerformance, benchmarkPerformance: IPortfolioPerformance): boolean => {
        const periods: PerformancePeriodKey[] = ["quarter", "ytd", "year", "threeYears", "fiveYears", "tenYears"];

        return periods.some(
            (period) => totalPlanAbsolutePerformance[period] && benchmarkPerformance[period]
        );
    };

export const mapPerformanceChartData = (
        totalPlanAbsolutePerformance: IPortfolioPerformance,
        benchmarkPerformance: IPortfolioPerformance
    ): IBarChartData[]  => {
        if (!hasValidPerformanceData(totalPlanAbsolutePerformance, benchmarkPerformance)) {
            return [];
        }

        const periods: { name: string; key: PerformancePeriodKey }[] = [
            { name: "Quarter", key: "quarter" },
            { name: "Year To Date", key: "ytd" },
            { name: "1 Year", key: "year" },
            { name: "3 Year", key: "threeYears" },
            { name: "5 Year", key: "fiveYears" },
            { name: "10 Year", key: "tenYears" }
        ];
        return periods.map(({ name, key }) => ({
            name,
            absolutePerformanceValue: totalPlanAbsolutePerformance?.[key] ?? "N/A",
            benchmarkPerformanceValue: benchmarkPerformance?.[key] ?? "N/A"
        }));
};
